import React, { useState, useEffect } from "react";
import cancel from "../../assets/images/navbar/cancel.png";
import {
  allergenService,
  cuisineService,
  dietaryLabelService,
  foodTypeService,
  mealTypeService,
  spiceService,
  tagsService,
} from "../../services";
import MultiSelectDropdown from "../MultiSelectDropdown";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";

interface DishForm2Props {
  onNext: (data: DishForm2Data) => void;
  onPrevious: () => void;
  onChange?: (data: Partial<DishForm2Data>) => void;
  initialData?: DishForm2Data;
  cuisineTypes?: Array<{ id: string; name: string }>;
  allergenOptions?: Array<{ id: string; name: string }>;
  dietaryLabels?: Array<{ id: string; name: string }>;
}

export interface DishForm2Data {
  foodType: "veg" | "non-veg" | "egg" | "";
  cuisineType: string;
  mealType: string;
  spiceLevel: "mild" | "medium" | "hot" | "extra-hot" | "";
  tags: string[];
  allergenInfo: string[];
  dietaryLabels: string[];
}

const DishForm2: React.FC<DishForm2Props> = ({
  onNext,
  onPrevious,
  onChange,
  initialData,
  cuisineTypes = [],
  allergenOptions = [],
  dietaryLabels = [],
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<DishForm2Data>({
    foodType: initialData?.foodType || "",
    cuisineType: initialData?.cuisineType || "",
    mealType: initialData?.mealType || "",
    spiceLevel: initialData?.spiceLevel || "",
    tags: initialData?.tags || [],
    allergenInfo: initialData?.allergenInfo || [],
    dietaryLabels: initialData?.dietaryLabels || [],
  });

  const [tagInput, setTagInput] = useState("");
  const [showTagInput, setShowTagInput] = useState(false);
  const [error, setError] = useState("");
  const [fetchedCuisines, setFetchedCuisines] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [foodTypes, setFoodTypes] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [mealTypes, setMealTypes] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [spiceLevels, setSpiceLevels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [tags, setTags] = useState<Array<{ id: string; name: string }>>([]);
  const [allergen, setAllergen] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [dietaryLabel, setDietaryLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        foodType: initialData.foodType || "",
        cuisineType: initialData.cuisineType || "",
        mealType: initialData.mealType || "",
        spiceLevel: initialData.spiceLevel || "",
        tags: initialData.tags || [],
        allergenInfo: initialData.allergenInfo || [],
        dietaryLabels: initialData.dietaryLabels || [],
      });
    }
  }, [initialData]);

  // Fetch cuisines on component mount
  useEffect(() => {
    const fetchCuisines = async () => {
      try {
        const response: any = await cuisineService.getCuisines();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedCuisines = response.data.map((cuisine: any) => ({
            id: cuisine._id,
            name: cuisine.name,
          }));
          setFetchedCuisines(mappedCuisines);
        }
      } catch (error: any) {
        console.error("Error fetching cuisines:", error);
        toast.error(error.message || "Failed to load cuisines");
      }
    };

    fetchCuisines();
  }, []);

  // Fetch food types on component mount
  useEffect(() => {
    const fetchFoodTypes = async () => {
      try {
        const response: any = await foodTypeService.getFoodTypes();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedFoodTypes = response.data.map((foodType: any) => ({
            id: foodType._id,
            name: foodType.name,
          }));
          setFoodTypes(mappedFoodTypes);
        }
      } catch (error: any) {
        console.error("Error fetching food types:", error);
        toast.error(error.message || "Failed to load food types");
      }
    };

    fetchFoodTypes();
  }, []);

  // Fetch meal types on component mount
  useEffect(() => {
    const fetchMealTypes = async () => {
      try {
        const response: any = await mealTypeService.getMealTypes();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedMealTypes = response.data.map((mealType: any) => ({
            id: mealType._id,
            name: mealType.name,
          }));
          setMealTypes(mappedMealTypes);
        }
      } catch (error: any) {
        console.error("Error fetching meal types:", error);
        toast.error(error.message || "Failed to load meal types");
      }
    };

    fetchMealTypes();
  }, []);

  // Fetch spice levels on component mount
  useEffect(() => {
    const fetchSpiceLevels = async () => {
      try {
        const response: any = await spiceService.getSpiceLevels();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedSpiceLevels = response.data.map((spiceLevel: any) => ({
            id: spiceLevel._id,
            name: spiceLevel.name,
          }));
          setSpiceLevels(mappedSpiceLevels);
        }
      } catch (error: any) {
        console.error("Error fetching spice levels:", error);
        toast.error(error.message || "Failed to load spice levels");
      }
    };

    fetchSpiceLevels();
  }, []);

  // Fetch tags on component mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response: any = await tagsService.getTags();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedTags = response.data.map((tag: any) => ({
            id: tag._id,
            name: tag.name,
          }));
          setTags(mappedTags);
        }
      } catch (error: any) {
        console.error("Error fetching tags:", error);
        toast.error(error.message || "Failed to load tags");
      }
    };

    fetchTags();
  }, []);

  // fetch allergen
  useEffect(() => {
    const fetchAllergen = async () => {
      try {
        const response: any = await allergenService.getAllergens();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedAllergens = response.data.map((allergen: any) => ({
            id: allergen._id,
            name: allergen.name,
          }));
          setAllergen(mappedAllergens);
        }
      } catch (error: any) {
        console.error("Error fetching allergens:", error);
        toast.error(error.message || "Failed to load allergens");
      }
    };

    fetchAllergen();
  }, []);

  useEffect(() => {
    const fetchAllDietary = async () => {
      try {
        const response: any = await dietaryLabelService.getDietaryLabels();
        if (response.status && response.data) {
          // Map the response to match the expected format
          const mappedDietaryLabels = response.data.map((label: any) => ({
            id: label._id,
            name: label.name,
          }));
          setDietaryLabels(mappedDietaryLabels);
        }
      } catch (error: any) {
        console.error("Error fetching dietary labels:", error);
        toast.error(error.message || "Failed to load dietary labels");
      }
    };

    fetchAllDietary();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const updatedData = { [name]: value };
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    setError("");
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange(updatedData);
    }
  };

  const handleMealTypeChange = (mealType: string) => {
    setFormData((prev) => ({
      ...prev,
      mealType,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ mealType });
    }
  };
  const handleTagChange = (tag: string) => {
    setFormData((prev) => {
      const updatedTags = prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag];
      
      // Call onChange immediately to update parent state
      if (onChange) {
        onChange({ tags: updatedTags });
      }
      
      return {
        ...prev,
        tags: updatedTags,
      };
    });
  };

  const handleCuisineTypeChange = (cuisineId: string) => {
    setFormData((prev) => ({
      ...prev,
      cuisineType: cuisineId,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ cuisineType: cuisineId });
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      const updatedTags = [...formData.tags, tagInput.trim()];
      setFormData((prev) => ({
        ...prev,
        tags: updatedTags,
      }));
      setTagInput("");
      setShowTagInput(false);
      // Call onChange immediately to update parent state
      if (onChange) {
        onChange({ tags: updatedTags });
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = formData.tags.filter((tag) => tag !== tagToRemove);
    setFormData((prev) => ({
      ...prev,
      tags: updatedTags,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ tags: updatedTags });
    }
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.foodType) {
      setError("Please select a food type");
      return;
    }
    if (!formData.cuisineType) {
      setError("Please select a cuisine type");
      return;
    }
    if (!formData.mealType) {
      setError("Please select a meal type");
      return;
    }
    if (!formData.spiceLevel) {
      setError("Please select a spice level");
      return;
    }

    onNext(formData);
  };

  return (
    <div className="w-full px-3 sm:px-4 md:px-6">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Food Type */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Food Type
          </label>
          <select
            name="foodType"
            value={formData.foodType}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="" disabled>
              Select Food Type
            </option>
            {foodTypes.length > 0 &&
              foodTypes.map((foodType) => (
                <option key={foodType.id} value={foodType.id}>
                  {foodType.name}
                </option>
              ))}
          </select>
        </div>

        {/* Cuisine Type */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Cuisine Type
          </label>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {(fetchedCuisines.length > 0 ? fetchedCuisines : cuisineTypes)
              .length > 0 &&
              (fetchedCuisines.length > 0 ? fetchedCuisines : cuisineTypes).map(
                (cuisine) => (
                  <button
                    key={cuisine.id}
                    type="button"
                    onClick={() => handleCuisineTypeChange(cuisine.id)}
                    className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-Urbanist text-xs sm:text-[14px] font-medium transition-colors ${
                      formData.cuisineType === cuisine.id
                        ? "bg-[#FF8A00] text-white"
                        : isDarkMode
                          ? "bg-gray-700 border border-gray-600 text-gray-300 hover:border-[#FF8A00]"
                          : "bg-white border border-gray-300 text-[#6A6A6A] hover:border-[#FF8A00]"
                    }`}
                  >
                    {cuisine.name}
                  </button>
                ),
              )}
          </div>
        </div>

        {/* Meal Type */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Meal Type
          </label>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {mealTypes.length > 0 &&
              mealTypes.map((mealType) => (
                <button
                  type="button"
                  onClick={() => handleMealTypeChange(mealType.id)}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-Urbanist text-xs sm:text-[14px] font-medium transition-colors ${
                    formData.mealType === mealType.id
                      ? "bg-[#FF8A00] text-white"
                      : isDarkMode
                        ? "bg-gray-700 border border-gray-600 text-gray-300 hover:border-[#FF8A00]"
                        : "bg-white border border-gray-300 text-[#6A6A6A] hover:border-[#FF8A00]"
                  }`}
                >
                  {mealType.name}
                </button>
              ))}
          </div>
        </div>

        {/* Spice Level */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Spice Level
          </label>
          <select
            name="spiceLevel"
            value={formData.spiceLevel}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="" disabled>
              Select Spice level
            </option>
            {spiceLevels.length > 0 &&
              spiceLevels.map((spiceLevel) => (
                <option key={spiceLevel.id} value={spiceLevel.id}>
                  {spiceLevel.name}
                </option>
              ))}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Tags
          </label>
          {/* <div className="flex gap-4">
            {!showTagInput ? (
              <button
                type="button"
                onClick={() => setShowTagInput(true)}
                className="px-6 py-2 bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-[14px] font-medium rounded-lg transition-colors"
              >
                Add New
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  placeholder="Add tags"
                  autoFocus
                  className="w-[215px] px-4 py-3 border border-gray-300 rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-6 py-2 bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-[14px] font-medium rounded-lg transition-colors"
                >
                  OK
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTagInput(false);
                    setTagInput("");
                  }}
                  className="px-6 py-2 bg-white border border-gray-300 hover:border-gray-400 text-[#6A6A6A] font-Urbanist text-[14px] font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5F5F5] text-[#050505] rounded-lg font-Urbanist text-[18px] border font-medium leading-[160%]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className=""
                    >
                      <img
                        src={cancel}
                        alt="Cancel"
                        className="h-[20px] w-[20px]"
                      />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div> */}

          <div className="flex flex-wrap gap-2 sm:gap-3">
            {tags.length > 0 &&
              tags.map((tag) => (
                <button
                  type="button"
                  onClick={() => handleTagChange(tag.id)}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-Urbanist text-xs sm:text-[14px] font-medium transition-colors ${
                    formData.tags.includes(tag.id)
                      ? "bg-[#FF8A00] text-white"
                      : isDarkMode
                        ? "bg-gray-700 border border-gray-600 text-gray-300 hover:border-[#FF8A00]"
                        : "bg-white border border-gray-300 text-[#6A6A6A] hover:border-[#FF8A00]"
                  }`}
                >
                  {tag.name}
                </button>
              ))}
          </div>
        </div>

        {/* Allergen Info */}
        <MultiSelectDropdown
          label="Allergen Info"
          options={allergen}
          selectedValues={formData.allergenInfo}
          onChange={(selectedIds) => {
            setFormData((prev) => ({
              ...prev,
              allergenInfo: selectedIds,
            }));
            if (onChange) {
              onChange({ allergenInfo: selectedIds });
            }
          }}
          placeholder="Select allergen information"
          selectedBgColor="bg-red-50"
          selectedTextColor="text-red-700"
          selectedBorderColor="border-red-200"
          isDarkMode={isDarkMode}
        />

        {/* Dietary Labels */}
        <MultiSelectDropdown
          label="Dietary Labels"
          options={dietaryLabel}
          selectedValues={formData.dietaryLabels}
          onChange={(selectedIds) => {
            setFormData((prev) => ({
              ...prev,
              dietaryLabels: selectedIds,
            }));
            if (onChange) {
              onChange({ dietaryLabels: selectedIds });
            }
          }}
          placeholder="Select dietary labels"
          selectedBgColor="bg-green-50"
          selectedTextColor="text-green-700"
          selectedBorderColor="border-green-200"
          isDarkMode={isDarkMode}
        />

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm font-Urbanist">{error}</div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            type="button"
            onClick={onPrevious}
            className={`w-full md:w-auto md:min-w-[280px] lg:w-[300px] sm:flex-1 border-2 hover:border-gray-400 font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 transition-colors rounded-full ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-[#050505]'
            }`}
          >
            Previous
          </button>
          <button
            type="submit"
            className="w-full sm:flex-1 md:w-auto md:min-w-[280px] lg:w-[300px] bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-lg sm:text-xl md:text-[25px] font-semibold py-3 sm:py-3.5 rounded-full sm:rounded-[188px] transition-colors leading-[140%]"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default DishForm2;
